import { motion } from 'framer-motion';
import { Dumbbell, Users, Target, Utensils, Monitor, Laptop } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useScrollReveal, staggerContainer, staggerItem } from '../hooks/useScrollReveal';

const Services = () => {
  const { ref, isInView } = useScrollReveal();
  const { t } = useLanguage();

  const services = [
    {
      icon: Dumbbell,
      ...t.services.crossfit
    },
    {
      icon: Users,
      ...t.services.group
    },
    {
      icon: Target,
      ...t.services.personal
    },
    {
      icon: Monitor,
      ...t.services.onlineAthlete
    },
    {
      icon: Laptop,
      ...t.services.onlineBasic
    },
    {
      icon: Utensils,
      ...t.services.nutrition
    }
  ];

  return (
    <section id="services" className="section-padding bg-black">
      <div className="container-max">
        <motion.div
          ref={ref}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
          className="reveal-section"
        >
          <motion.h2
            variants={staggerItem}
            className="text-4xl md:text-5xl font-bold text-center mb-16"
          >
            {t.services.title.includes(' & ') ? (
              <>
                {t.services.title.split(' & ')[0]} & <span className="text-primary-400">{t.services.title.split(' & ')[1]}</span>
              </>
            ) : (
              <span className="text-primary-400">{t.services.title}</span>
            )}
          </motion.h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                variants={staggerItem}
                className="bg-gradient-to-br from-gray-900 to-gray-800 p-6 rounded-lg border border-primary-500/20 hover:border-primary-400/40 transition-all duration-300 group hover:-translate-y-2"
              >
                <div className="mb-4">
                  <service.icon className="w-12 h-12 text-primary-400 group-hover:scale-110 transition-transform duration-300" />
                </div>
                <h3 className="text-xl font-semibold mb-3 text-white group-hover:text-primary-300 transition-colors">
                  {service.title}
                </h3>
                <p className="text-gray-300 mb-4 leading-relaxed">
                  {service.description}
                </p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="text-sm text-gray-400 flex items-center">
                      <div className="w-1.5 h-1.5 bg-primary-400 rounded-full mr-2"></div>
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          {/* Gallery Section */}
          <motion.div
            variants={staggerItem}
            className="mt-16"
          >
            <h3 className="text-2xl font-semibold text-center mb-8">{t.services.galleryTitle}</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="relative overflow-hidden rounded-lg group">
                <img
                  src="/IMG_4.webp"
                  alt="CrossFit training"
                  className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-110"
                  loading="lazy"
                  width="576"
                  height="384"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
              </div>

              <div className="relative overflow-hidden rounded-lg group">
                <img
                  src="/IMG_2937.webp"
                  alt="Weightlifting"
                  className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-110"
                  loading="lazy"
                  width="288"
                  height="384"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
              </div>

              <div className="relative overflow-hidden rounded-lg group">
                <img
                  src="/763dbb1e0e78486fb1b2d83c3274caa2-scaled.webp"
                  alt="Martin's journey"
                  className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-110"
                  loading="lazy"
                  width="577"
                  height="384"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
